import { Injectable, OnDestroy, inject } from '@angular/core'
import { Router } from '@angular/router'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { PosthogService } from '@app/services/posthog.service'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'
import { UserRoles } from '../enums/user-roles.enum'
import { AuthService } from './auth.service'

const ROLES_CAN_ADD = {
  accountant: [],
  viewer: [],
  approver: [],
  collaborator: [UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  admin: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  owner: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT]
}
const ROLES_CAN_DELETE = {
  accountant: [],
  viewer: [],
  approver: [],
  collaborator: [UserRoles.VIEWER],
  admin: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  owner: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT]
}

const ROLES_CAN_CHANGE = {
  accountant: [],
  viewer: [],
  approver: [],
  collaborator: [UserRoles.VIEWER],
  admin: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  owner: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT]
}

const WORKSPACE_OWNER_ROLE = 'owner'
@Injectable({
  providedIn: 'root'
})
export class WorkspaceRoleService implements OnDestroy {
  private _currentUserId: number
  private _role: UserRoles = UserRoles.APPROVER
  private _role$: ReplaySubject<string> = new ReplaySubject(1)
  private _destroy$: Subject<void> = new Subject()
  private _router = inject(Router)

  constructor(private auth: AuthService, private _posthogService: PosthogService) {
    this._currentUserId = this.auth.user?.id ?? 0
    this.auth.userChange.pipe(takeUntil(this._destroy$)).subscribe((user) => {
      if (!user) return
      this._currentUserId = user?.id
      this.updateUserRole()
    })
    this.auth.currentWorkspaceChange.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.updateUserRole()
    })
  }

  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Checks if user is superadmin
   * @returns boolean
   */
  isSuperAdmin(): boolean {
    return this.auth.isAdmin
  }

  /**
   * Gets the current user role on the workspace
   * @returns Subject<string>
   */
  getUserRole(): Subject<string> {
    return this._role$
  }

  /**
   * Gets the current authenticated user id
   * @returns number
   */
  getCurrentUserId(): number {
    return this._currentUserId
  }

  /**
   * Updates the user role based on the current workspace
   */
  private updateUserRole() {
    if (this.isSuperAdmin()) {
      this._role = UserRoles.ADMIN
      this._role$.next(this._role)
      this._posthogService.setWorkspace(this.auth.currentWorkspace, 'Super User')
      return
    }
    const workspace = this.auth.currentWorkspace
    if (!workspace?.id) return

    const users = workspace.workspace_users
    const user = users?.find((workspaceUser) => workspaceUser?.user_id === this._currentUserId) ?? users?.find((workspaceUser) => workspaceUser?.user?.id === this._currentUserId)

    this._role = user?.is_owner ? 'owner' : user?.role || null
    this._role$.next(this._role)

    if (this.auth.isAdmin) {
      this.auth.userRoleInCurrentWorksapce.next('admin')
    } else if (user) {
      this.auth.userRoleInCurrentWorksapce.next(user.role || (user.is_owner ? 'owner' : null))
    } else {
      this.auth.userRoleInCurrentWorksapce.next(null)
      if (this.auth.currentWorkspace?.id) {
        this._posthogService.setWorkspace(this.auth.currentWorkspace, this._role)
      }
    }
    this._posthogService.setWorkspace(workspace, this._role)
  }
  /**
   * Recompute the current workspace role from workspace_users (synchronous).
   * Call before role-based navigation decisions after switching organizations.
   */
  public syncRoleFromCurrentWorkspace(): void {
    this.updateUserRole()
  }

  /**
   * Navigate to events list, or organization settings billing when the user is an accountant in the current workspace.
   * Re-syncs role first so switches between orgs always use the role for the newly selected org.
   * Skips navigation when already on that path so landing cannot bounce through RoleGuard.
   */
  public navigateToWorkspaceLanding(): void {
    this.syncRoleFromCurrentWorkspace()

    const targetUrl = !this.auth.isAdmin && this.isAccountant() ? this.auth.getOrganizationBillingUrl() : '/events/list'
    if (!targetUrl || this._isSameLandingPath(this._router.url, targetUrl)) {
      return
    }

    this._router.navigateByUrl(targetUrl)
  }

  /**
   * POSTs last-active for the workspace, applies it locally, then navigates to events/list or billing.
   * Use for single-org auto-redirect and any path that activates a workspace then lands the user.
   * @param workspace Workspace to mark last-active and land into
   */
  public activateAndNavigateToLanding$(workspace: Workspace): Observable<unknown> {
    return this.auth.switchActiveOrganization$(workspace).pipe(tap(() => this.navigateToWorkspaceLanding()))
  }

  /**
   * True when two URLs share the same path (query and hash ignored).
   * @param currentUrl Current router URL
   * @param targetUrl Intended landing URL
   */
  private _isSameLandingPath(currentUrl: string, targetUrl: string): boolean {
    const normalize = (url: string) => url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/'
    return normalize(currentUrl) === normalize(targetUrl)
  }
  /**
   * Return the current user role based on the provided workspace users list
   * @param workspaceUsers any[]
   * @returns string | null
   */
  getUserRoleFromWorkspaceUsers(workspaceUsers: any[]): string | null {
    if (this.isSuperAdmin()) return UserRoles.ADMIN
    const user = workspaceUsers?.find((workspaceUser) => workspaceUser.user_id === this._currentUserId)
    if (!user) return null
    return user.is_owner ? WORKSPACE_OWNER_ROLE : user.role
  }

  /**
   * Collaborators can edit. Timeline already returns only events this user may access
   * (all org events, or assigned events when `is_limited_access`). No extra client filter.
   * @returns boolean
   */
  canEditEvent(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER, UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Owner, admin, and full-access collaborators (`is_limited_access` false).
   * Limited collaborators edit assigned events; they do not create them.
   * @returns boolean
   */
  canCreateEvent(): boolean {
    return this._canManageEventLifecycle()
  }

  /**
   * Same rule as create: owner, admin, and full-access collaborators.
   * @returns boolean
   */
  canCloneEvent(): boolean {
    return this._canManageEventLifecycle()
  }

  /**
   * Limited collaborators cannot archive. Full-access collaborators can.
   * Other roles keep the archive entry they already had.
   * @returns boolean
   */
  canArchiveEvent(): boolean {
    if (this._role === UserRoles.COLLABORATOR) return this._isFullAccessCollaborator()
    return true
  }

  /**
   * Limited collaborators cannot restore. Full-access collaborators can.
   * Other roles keep restore.
   * @returns boolean
   */
  canRestoreEvent(): boolean {
    if (this._role === UserRoles.COLLABORATOR) return this._isFullAccessCollaborator()
    return true
  }

  /**
   * Header "Archived items". Same audience as the old create check: owner, admin, collaborator.
   * @returns boolean
   */
  canOpenArchivedItems(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER, UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * No collaborator can create a sub-organization under the current organization.
   * Applies to full-access (`is_limited_access` false) and limited collaborators.
   * @returns boolean
   */
  canCreateSubOrganization(): boolean {
    if (this.isSuperAdmin()) return false
    return this._role !== UserRoles.COLLABORATOR
  }

  /**
   * Checks if user can add a user to a workspace
   * @returns boolean
   */
  canAddUsers(role: string = this._role): boolean {
    return ['owner', UserRoles.ADMIN, UserRoles.COLLABORATOR].includes(role)
  }

  /**
   * Checks if user can access webhooks.
   * Requires a confirmed email, then owner/admin/superadmin.
   * @returns boolean
   */
  public canAccessWebhooks(): boolean {
    if (!this.auth.user?.confirmed) return false
    if (this.isSuperAdmin()) return true
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * Checks if user can access API keys.
   * Requires a confirmed email, then owner/admin/superadmin.
   * @returns boolean
   */
  public canAccessApiKeys(): boolean {
    if (!this.auth.user?.confirmed) return false
    if (this.isSuperAdmin()) return true
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * List of roles that the user can assign to other users
   * @param role Current user role
   * @returns boolean
   */
  rolesListToAdd(role: string = this._role): string[] {
    return ROLES_CAN_ADD[role]
  }

  /**
   * List of roles that the user can assign to other users
   * @param role Current user role
   * @returns boolean
   */
  rolesListToDelete(role: string = this._role): string[] {
    return ROLES_CAN_DELETE[role]
  }

  /**
   * List of roles that the user can change for other users
   * @param role Current user role
   * @returns boolean
   */
  rolesListToChange(role: string = this._role): string[] {
    return ROLES_CAN_CHANGE[role]
  }

  /**
   * Checks if the current user can delete a workspace member with the given role
   * @param targetRole Role of the member being removed
   * @param actorRole Role of the acting user
   * @returns boolean
   */
  canDeleteUser(targetRole: string, actorRole: string = this._role): boolean {
    if (targetRole === WORKSPACE_OWNER_ROLE) return false
    if (this.isSuperAdmin()) return true

    const allowedRoles = ROLES_CAN_DELETE[actorRole]
    if (!allowedRoles?.length) return false
    return allowedRoles.includes(targetRole)
  }

  /**
   * Checks if the current user can change status for a workspace member with the given role
   * @param targetRole Role of the member being updated
   * @param actorRole Role of the acting user
   * @returns boolean
   */
  canChangeUser(targetRole: string, actorRole: string = this._role): boolean {
    if (targetRole === WORKSPACE_OWNER_ROLE) return false
    if (this.isSuperAdmin()) return true

    const allowedRoles = ROLES_CAN_CHANGE[actorRole]
    if (!allowedRoles?.length) return false
    return allowedRoles.includes(targetRole)
  }

  /**
   * Checks if the current user can assign a specific role to a target user.
   * @param targetRole Current role of the target user
   * @param nextRole Role to be assigned
   * @param actorRole Role of the acting user
   * @returns boolean
   */
  canAssignRole(targetRole: string, nextRole: string, actorRole: string = this._role): boolean {
    if (targetRole === WORKSPACE_OWNER_ROLE || nextRole === WORKSPACE_OWNER_ROLE) return false

    if (this.isSuperAdmin()) return true

    // Collaborators may only invite/keep viewers; they cannot promote to elevated roles.
    if (actorRole === UserRoles.COLLABORATOR) {
      return targetRole === UserRoles.VIEWER && nextRole === UserRoles.VIEWER
    }

    if (actorRole === WORKSPACE_OWNER_ROLE || actorRole === UserRoles.ADMIN) {
      return this.canChangeUser(targetRole, actorRole) && this.rolesListToAdd(actorRole).includes(nextRole)
    }

    return false
  }

  /**
   * Checks if user can view the database
   * @returns boolean
   */
  canViewDatabase(): boolean {
    return ![UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can edit the database
   * @returns boolean
   */
  canEditDatabase(): boolean {
    return ![UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can export an event's database
   * @returns boolean
   */
  canExportDatabase(): boolean {
    return ['owner', UserRoles.ADMIN, UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can import to an event's database
   * @returns boolean
   */
  canImportToEvent(): boolean {
    return ['owner', UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can export master database
   * @returns boolean
   */
  canExportMasterDatabase(): boolean {
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * Checks if user can add creditcard
   * @returns boolean
   */
  canAddCreditcard(): boolean {
    return this.canEditBilling()
  }

  /**
   * Checks if user can remove creditcard
   * @returns boolean
   */
  canRemoveCreditcard(): boolean {
    return this.canEditBilling()
  }

  /**
   * Checks if user can upgrade workspace
   * @returns boolean
   */
  canUpgradeWorkspace(): boolean {
    return this.canEditBilling() && !this.auth.user?.is_admin
  }

  /**
   * Checks if user can manage the subscription / plan (customer portal).
   * @returns boolean
   */
  canEditSubscription(): boolean {
    return this.canEditBilling()
  }

  /**
   * Whether the user can edit billing (add/remove payment cards, manage plan).
   * Collaborators can access billing but cannot edit.
   * @returns {boolean} True for accountant, owner, admin, or superadmin
   */
  canEditBilling(): boolean {
    if (this.isSuperAdmin()) return true

    return ['owner', UserRoles.ADMIN, UserRoles.ACCOUNTANT].includes(this._role as string)
  }

  /**
   * Whether the user can see the billing section and top up the wallet.
   * Includes collaborators (view + top-up only).
   * @returns {boolean} True for collaborator, accountant, owner, admin, or superadmin
   */
  canAccessBilling(): boolean {
    if (this.isSuperAdmin()) return true

    return ['owner', UserRoles.ADMIN, UserRoles.ACCOUNTANT, UserRoles.COLLABORATOR].includes(this._role as string)
  }

  /**
   * Whether the current workspace role is accountant (billing-only org settings access).
   * Superadmins always return false so they keep full org-settings access.
   * @returns {boolean} True when the user is an accountant and not a superadmin
   */
  isAccountant(): boolean {
    if (this.isSuperAdmin()) return false

    return this._role === UserRoles.ACCOUNTANT
  }

  /**
   * Checks if user can change approval status
   * @returns boolean
   */
  canChangeApprovalStatus(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can see the actions column in the database
   * @returns boolean
   */
  canSeeActionsColumn(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can share events/personas in the timeline
   * @returns boolean
   */
  canShareInTimeline(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Organization settings.
   * Viewers and approvers cannot. Limited collaborators (`is_limited_access`) cannot.
   * Full-access collaborators can.
   * @returns boolean
   */
  canAccessWorkspaceSettings(): boolean {
    if ([UserRoles.VIEWER, UserRoles.APPROVER].includes(this._role)) return false
    if (this._role === UserRoles.COLLABORATOR && this._currentMemberIsLimitedAccess()) return false
    return true
  }

  /**
   * Checks if user can access workspace members list
   * @returns boolean
   */
  canAccessWorkspaceMembers(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER].includes(this._role)
  }

  /**
   * Checks if user can access bulk actions (for selecting rows)
   * @returns boolean
   */
  canAccessBulkActions(): boolean {
    return ![UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can access flags menu
   * @returns boolean
   */
  canAccessFlagsMenu(): boolean {
    return true // All roles can access flags menu
  }
  //  * Checks if user can edit registration limits
  //  * @returns boolean
  //  */
  canEditRegistrationLimit(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can delete archived events
   * @returns boolean
   */
  canDeleteArchivedEvents(): boolean {
    return this._canManageEventLifecycle()
  }

  /**
   * Checks if user can archive items
   * @returns boolean
   */
  canArchiveItems(): boolean {
    return this._canManageEventLifecycle()
  }

  /**
   * Create, clone, and delete events.
   * Full-access collaborators (`is_limited_access` false) included.
   * Limited collaborators are not.
   */
  private _canManageEventLifecycle(): boolean {
    if (this.isSuperAdmin()) return true
    if (this._isFullAccessCollaborator()) return true
    return [WORKSPACE_OWNER_ROLE, UserRoles.ADMIN].includes(this._role as string)
  }

  /**
   * Collaborator with organization-wide access. Limited members stay on assigned events.
   */
  private _isFullAccessCollaborator(): boolean {
    return this._role === UserRoles.COLLABORATOR && !this._currentMemberIsLimitedAccess()
  }

  /**
   * Checks if user cannot access manage team
   * @returns boolean
   */
  canAccessManageTeam(): boolean {
    if (this.isSuperAdmin()) return true
    return !([UserRoles.VIEWER, UserRoles.ACCOUNTANT] as string[]).includes(this._role)
  }
  /**
   * Checks if user can modify event or personas
   * @returns boolean
   */
  canModifyEventOrPersonas(): boolean {
    if (this.isSuperAdmin()) return true
    return !([UserRoles.VIEWER, UserRoles.ACCOUNTANT] as string[]).includes(this._role)
  }

  /**
   * Timeline "Grant limited access".
   * Hidden for viewers and for collaborators with is_limited_access on their workspace_users row.
   * Same object as role: workspace.workspace_users[].is_limited_access.
   */
  public canGrantLimitedAccess(): boolean {
    if (this.isSuperAdmin()) return true
    if (this._role === UserRoles.VIEWER) return false
    if (this._role === UserRoles.COLLABORATOR && this._currentMemberIsLimitedAccess()) return false
    return true
  }

  /**
   * Current user's membership on the active workspace.
   */
  private _currentWorkspaceMember() {
    const users = this.auth.currentWorkspace?.workspace_users
    return users?.find((workspaceUser) => workspaceUser?.user_id === this._currentUserId) ?? users?.find((workspaceUser) => workspaceUser?.user?.id === this._currentUserId)
  }

  /**
   * True when current membership row is limited to specific events
   * (`workspace.workspace_users[].is_limited_access`).
   */
  public isLimitedAccess(): boolean {
    return this._currentMemberIsLimitedAccess()
  }

  /**
   * True when this membership row is limited to specific events.
   */
  private _currentMemberIsLimitedAccess(): boolean {
    return !!this._currentWorkspaceMember()?.is_limited_access
  }
}
